import { Calendar, Lightbulb } from "lucide-react";
import {
  addClinicDays,
  clinicDateAt,
  clinicDateStringFromInstant,
  clinicTodayDateString,
} from "@/lib/clinicTime";

// =====================================================================
// FollowupPicker — spec §10
//
// 1 week / 2 weeks / 1 month presets + custom date.
// `value` is an ISO timestamp string (or null). `onChange(iso)` is called
// when a preset or custom date is picked.
// =====================================================================

const PRESETS = [
  { key: "1w",  label: "In 1 week",   days: 7  },
  { key: "2w",  label: "In 2 weeks",  days: 14 },
  { key: "1mo", label: "In 1 month",  days: 30 },
];

function isoForDaysFromNow(days) {
  return clinicDateAt(addClinicDays(clinicTodayDateString(), days), 9, 0, 0).toISOString();
}

function inputDateValue(iso) {
  if (!iso) return "";
  return clinicDateStringFromInstant(iso);
}

function isoFromInputDate(s) {
  if (!s) return null;
  const d = clinicDateAt(s, 9, 0, 0);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function presetMatches(iso, days) {
  if (!iso) return false;
  const want = clinicDateAt(addClinicDays(clinicTodayDateString(), days), 9, 0, 0);
  return Math.abs(new Date(iso).getTime() - want.getTime()) < 60 * 1000;
}

export default function FollowupPicker({ firstName, value, onChange }) {
  return (
    <div className="mt-3 space-y-3" data-testid="recall-followup-picker" data-tutorial-id="recall-followup-picker">
      <div className="bg-amber-50/60 border border-amber-200 rounded-lg p-2.5 text-[12px] text-amber-900 inline-flex items-start gap-2 w-full">
        <Lightbulb className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
        <div className="font-medium">💡 Say: {`"Should I check back in 2 weeks, or would a month feel better?"`}</div>
      </div>

      <div>
        <div className="text-[12px] font-semibold text-slate-700 mb-2">When should we follow up?</div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => {
            const active = presetMatches(value, p.days);
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => onChange(isoForDaysFromNow(p.days))}
                data-testid={`recall-followup-preset-${p.key}`}
                data-tutorial-id={`recall-followup-preset-${p.key}`}
                className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                  active
                    ? "bg-teal-50 text-teal-700 border-teal-200 ring-2 ring-offset-1 ring-teal-200"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="text-[12px] font-semibold text-slate-700 block mb-1">
          Or pick a date
        </label>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="date"
            value={inputDateValue(value)}
            onChange={(e) => onChange(isoFromInputDate(e.target.value))}
            min={clinicTodayDateString()}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
            data-testid="recall-followup-date"
            data-tutorial-id="recall-followup-date"
          />
        </div>
        {firstName ? (
          <p className="text-[11px] text-slate-500 mt-1">
            We'll reach out to {firstName} on the date you pick.
          </p>
        ) : null}
      </div>
    </div>
  );
}
